import { useLocation, useNavigate } from 'react-router-dom';
import { useCallback } from 'react';

export type Locale = 'zh' | 'en' | 'ja';

export const LOCALES: { code: Locale; label: string; short: string }[] = [
  { code: 'zh', label: '繁體中文', short: '中文' },
  { code: 'en', label: 'English', short: 'EN' },
  { code: 'ja', label: '日本語', short: 'JP' },
];

const LOCALE_PREFIXES: Record<Locale, string> = {
  zh: '',
  en: '/en',
  ja: '/ja',
};

/**
 * All valid base paths (without locale prefix).
 * Used to validate if the target path exists before navigating.
 */
const VALID_BASE_PATHS = new Set([
  '/',
  '/home/core',
  '/home/advantages',
  '/home/experience',
  '/home/hot-formulas',
  '/about',
  '/about/core-advantages',
  '/about/production',
  '/about/cases',
  '/about/regulation',
  '/partnership',
  '/cooperation/process',
  '/cooperation/services',
  '/cooperation/faq',
  '/cooperation/inquiry',
  '/services',
  '/services/hot-formulas',
  '/services/natural-ingredients',
  '/services/dosage-forms',
  '/services/packaging',
  '/contact',
  '/contact/form',
  '/contact/info',
  '/privacy-policy',
  '/terms-of-use',
]);

/** 從當前路徑解析語系與去掉前綴後的 basePath */
export function parseLocale(pathname: string): { locale: Locale; basePath: string } {
  if (pathname === '/en' || pathname.startsWith('/en/')) {
    const base = pathname.slice(3);
    return { locale: 'en', basePath: base || '/' };
  }
  if (pathname === '/ja' || pathname.startsWith('/ja/')) {
    const base = pathname.slice(3);
    return { locale: 'ja', basePath: base || '/' };
  }
  return { locale: 'zh', basePath: pathname || '/' };
}

/** 把 basePath 加上語系前綴 */
export function buildLocalePath(locale: Locale, basePath: string): string {
  const prefix = LOCALE_PREFIXES[locale];
  const path = basePath || '/';
  if (path === '/') return prefix || '/';
  return `${prefix}${path}`;
}

export default function useLocale() {
  const location = useLocation();
  const navigate = useNavigate();
  const { locale, basePath } = parseLocale(location.pathname);

  const switchLocale = useCallback(
    (next: Locale) => {
      if (next === locale) return;
      // Check if the target base path is valid, otherwise fall back to locale root
      const targetBase = VALID_BASE_PATHS.has(basePath) ? basePath : '/';
      const target = buildLocalePath(next, targetBase);
      navigate(target);
    },
    [locale, basePath, navigate],
  );

  return { locale, basePath, switchLocale };
}
